import { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Building2, Mail, Phone, DollarSign, Calendar, Target } from 'lucide-react';
import axios from 'axios';
import brand from '../config/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { slug: 'news', name: 'News' },
  { slug: 'personal-finance', name: 'Personal Finance' },
  { slug: 'debt-management', name: 'Debt Management' },
  { slug: 'small-business', name: 'Small Business' },
  { slug: 'investing', name: 'Investing' },
  { slug: 'markets', name: 'Markets' },
];

const budgetRanges = [
  '$1,000 - $5,000',
  '$5,000 - $10,000',
  '$10,000 - $25,000',
  '$25,000 - $50,000',
  '$50,000+',
];

const adTypes = [
  'Banner Ads',
  'Sponsored Content',
  'Newsletter Placement',
  'Homepage Takeover',
  'Video Ads',
  'Native Advertising',
];

const durations = [
  '1 Week',
  '2 Weeks',
  '1 Month',
  '3 Months',
  '6 Months',
  '1 Year',
];

export default function AdvertiserPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    budget_range: '',
    ad_type: '',
    target_categories: [],
    campaign_duration: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryToggle = (slug) => {
    setFormData((prev) => ({
      ...prev,
      target_categories: prev.target_categories.includes(slug)
        ? prev.target_categories.filter((c) => c !== slug)
        : [...prev.target_categories, slug],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.company_name || !formData.contact_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.target_categories.length === 0) {
      toast.error('Please select at least one target category');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/advertiser/request`, formData);
      toast.success('Your request has been submitted! We will contact you soon.');
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        budget_range: '',
        ad_type: '',
        target_categories: [],
        campaign_duration: '',
        message: '',
      });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617]" data-testid="advertiser-page">
      <StockTicker />
      <Navbar />

      {/* Header */}
      <header className="py-16 border-b border-[#1E293B]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-slate-50 mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Advertise With Us
          </h1>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">
            Reach thousands of finance-focused readers. Partner with {brand.name} to promote your brand, 
            products, or services to our engaged audience.
          </p>
        </div>
      </header>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-[#0F172A] p-8 rounded-none border border-[#1E293B]">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Company Information */}
              <div>
                <h3
                  className="text-xl font-bold text-slate-100 mb-6 flex items-center"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <Building2 className="w-5 h-5 mr-2 text-[#D4AF37]" strokeWidth={1.5} />
                  Company Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                      Company Name *
                    </Label>
                    <Input
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      className="bg-[#020617] border-[#1E293B] focus:border-[#D4AF37] text-slate-100 placeholder:text-slate-600 rounded-none"
                      placeholder="Your Company"
                      data-testid="advertiser-company-name"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                      Contact Name *
                    </Label>
                    <Input
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      className="bg-[#020617] border-[#1E293B] focus:border-[#D4AF37] text-slate-100 placeholder:text-slate-600 rounded-none"
                      placeholder="John Doe"
                      data-testid="advertiser-contact-name"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3
                  className="text-xl font-bold text-slate-100 mb-6 flex items-center"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <Mail className="w-5 h-5 mr-2 text-[#D4AF37]" strokeWidth={1.5} />
                  Contact Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-[#020617] border-[#1E293B] focus:border-[#D4AF37] text-slate-100 placeholder:text-slate-600 rounded-none"
                      placeholder="contact@company.com"
                      data-testid="advertiser-email"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                      Phone Number
                    </Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-[#020617] border-[#1E293B] focus:border-[#D4AF37] text-slate-100 placeholder:text-slate-600 rounded-none"
                      placeholder="+1 (555) 000-0000"
                      data-testid="advertiser-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Campaign Details */}
              <div>
                <h3
                  className="text-xl font-bold text-slate-100 mb-6 flex items-center"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <DollarSign className="w-5 h-5 mr-2 text-[#D4AF37]" strokeWidth={1.5} />
                  Campaign Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                      Budget Range
                    </Label>
                    <Select
                      value={formData.budget_range}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, budget_range: value }))}
                    >
                      <SelectTrigger className="bg-[#020617] border-[#1E293B] focus:border-[#D4AF37] text-slate-100 rounded-none" data-testid="advertiser-budget">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-[#1E293B] shadow-xl shadow-black/50 z-[100]">
                        {budgetRanges.map((range) => (
                          <SelectItem key={range} value={range} className="text-slate-300 hover:bg-[#1E293B] focus:bg-[#1E293B] focus:text-[#D4AF37] cursor-pointer">
                            {range}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                      Ad Type
                    </Label>
                    <Select
                      value={formData.ad_type}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, ad_type: value }))}
                    >
                      <SelectTrigger className="bg-[#020617] border-[#1E293B] focus:border-[#D4AF37] text-slate-100 rounded-none" data-testid="advertiser-ad-type">
                        <SelectValue placeholder="Select ad type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-[#1E293B] shadow-xl shadow-black/50 z-[100]">
                        {adTypes.map((type) => (
                          <SelectItem key={type} value={type} className="text-slate-300 hover:bg-[#1E293B] focus:bg-[#1E293B] focus:text-[#D4AF37] cursor-pointer">
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Campaign Duration */}
              <div>
                <h3
                  className="text-xl font-bold text-slate-100 mb-6 flex items-center"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <Calendar className="w-5 h-5 mr-2 text-[#D4AF37]" strokeWidth={1.5} />
                  Campaign Duration
                </h3>
                <Select
                  value={formData.campaign_duration}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, campaign_duration: value }))}
                >
                  <SelectTrigger className="bg-[#020617] border-[#1E293B] focus:border-[#D4AF37] text-slate-100 rounded-none" data-testid="advertiser-duration">
                    <SelectValue placeholder="Select campaign duration" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F172A] border-[#1E293B] shadow-xl shadow-black/50 z-[100]">
                    {durations.map((duration) => (
                      <SelectItem key={duration} value={duration} className="text-slate-300 hover:bg-[#1E293B] focus:bg-[#1E293B] focus:text-[#D4AF37] cursor-pointer">
                        {duration}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Target Categories */}
              <div>
                <h3
                  className="text-xl font-bold text-slate-100 mb-6 flex items-center"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <Target className="w-5 h-5 mr-2 text-[#D4AF37]" strokeWidth={1.5} />
                  Target Categories *
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {categories.map((cat) => (
                    <div key={cat.slug} className="flex items-center space-x-2">
                      <Checkbox
                        id={cat.slug}
                        checked={formData.target_categories.includes(cat.slug)}
                        onCheckedChange={() => handleCategoryToggle(cat.slug)}
                        className="border-[#334155] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                        data-testid={`advertiser-category-${cat.slug}`}
                      />
                      <Label htmlFor={cat.slug} className="text-sm text-slate-300 cursor-pointer">
                        {cat.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                  Additional Message
                </Label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="bg-[#020617] border-[#1E293B] focus:border-[#D4AF37] text-slate-100 placeholder:text-slate-600 min-h-[120px] rounded-none"
                  placeholder="Tell us about your campaign goals, target audience, or any specific requirements..."
                  data-testid="advertiser-message"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-[#020617] hover:bg-[#B8941F] font-bold uppercase tracking-widest py-3 rounded-none"
                data-testid="advertiser-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
